import type { FlowExecutionBadgeVariant, FlowExecutionStatus } from '@/shared/types/flowExecution';

import { TRACO } from './placeholder';

/**
 * `Record<FlowExecutionStatus, …>` e não `Record<string, …>`: o core ganhou `CANCELLED` (A-45) e,
 * com a chave solta, o valor novo caía no fallback e aparecia cru («CANCELLED») no card. Tipado
 * pelo enum, o próximo valor esquecido vira erro de compilação.
 */
const statusVariantMap: Record<FlowExecutionStatus, FlowExecutionBadgeVariant> = {
  SUCCESS: 'success',
  ERROR: 'error',
  WAITING: 'warning',
  RUNNING: 'brand',
  PENDING: 'neutral',
  CANCELLED: 'neutral',
};

const statusLabelMap: Record<FlowExecutionStatus, string> = {
  SUCCESS: 'Sucesso',
  ERROR: 'Erro',
  WAITING: 'Aguardando',
  RUNNING: 'Executando',
  PENDING: 'Pendente',
  CANCELLED: 'Cancelada',
};

function isFlowExecutionStatus(status: string): status is FlowExecutionStatus {
  return Object.hasOwn(statusLabelMap, status);
}

/**
 * Variante do `Badge` do status de uma execução de fluxo. Status que o web ainda não conhece sai
 * `neutral`. Fonte única para Flow Logs, sandbox e simulação: as cópias já divergiram em rótulo e cor.
 */
export function statusVariant(status: string): FlowExecutionBadgeVariant {
  return isFlowExecutionStatus(status) ? statusVariantMap[status] : 'neutral';
}

/** Rótulo do status de uma execução de fluxo; status que o web ainda não conhece aparece como veio. */
export function statusLabel(status: string): string {
  return isFlowExecutionStatus(status) ? statusLabelMap[status] : status;
}

/**
 * Duração de execução ou de nó do motor: milissegundos abaixo de 1 s, segundos com duas casas a
 * partir daí, e traço quando o motor não mediu.
 *
 * @example formatDuration(120) // '120ms'
 * @example formatDuration(1234) // '1.23s'
 */
export function formatDuration(durationMs: number | null): string {
  if (durationMs == null) return TRACO;
  if (durationMs < 1000) return `${durationMs}ms`;
  return `${(durationMs / 1000).toFixed(2)}s`;
}

/** Entrada ou saída de nó do motor: texto como veio, JSON indentado, e traço quando não há valor. */
export function formatJson(value: unknown): string {
  if (value === null || value === undefined) return TRACO;
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}
